import * as React from "react";
import { useEffect, useState } from "react";
import { LoadingSpinner } from "@arctheme-components/elements/LoadingSpinner/LoadingSpinner";
import useMobileBreakpoint from "@arctheme-hooks/useMobileBreakpoint";
import { HeaderSearchNoResults } from "./HeaderNoResults";
import { HeaderKeywords } from "./HeaderKeywords";
import { TabbedLayout } from "./Layouts/Tabbed";
import { StackedLayout } from "./Layouts/Stacked";
import useSearchSpring from "@arctheme-hooks/useSearchSpring";
import "./HeaderSearch.scss";
import {
  ReactifySearchProvider
} from "@usereactify/search";
import useReactify from "@src/hooks/useReactify";

export const HeaderSearch = ({ settings }) => {
  const isMobile = useMobileBreakpoint(settings.mobile_breakpoint);
  const { autocompleteSearch } = useSearchSpring(null);
  const { reactifySearch } = useReactify(null);

  const { search_settings } = settings;
  const [query, setQuery] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = React.useState("products");
  const [products, setProducts]: any = useState([]);
  const [productsTotal, setProductsTotal] = useState(0);
  const [articles, setArticles] = useState([]);
  const [articlesTotal, setArticlesTotal]: any = useState([]);
  const [categorySuggestions, setCategorySuggestions]: any = useState([]);
  const [keywordSuggestions, setKeywordSuggestions]: any = useState([]);

  const showSidebar = false;
  const showNavigationTabs = false;
  const showSeeAllProductsAtBottom = false;

  const layoutType = "stacked";

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
  };


  useEffect(() => {
    if (search_settings) {
      if (search_settings.keywords) {
        const keywordsArray = search_settings.keywords
          .split(",")
          .map((keyword) => keyword.trim());
        setKeywordSuggestions(keywordsArray);
      }
    }

    const handleSearchRequest = (event) => {
      const { query:event_query } = event.detail;
      autocompleteSearch(event_query);
    }

    const handleProductsResults = (event) => {
      if (event.detail.type === "header") {
        const { query: event_query, totalResults: event_results, products: event_products } = event.detail;
        setQuery(event_query);
        setProducts(event_products);

        setProductsTotal(event_results);
        window.dispatchEvent(
          new CustomEvent("hide-spinner", {
            detail: {
              id: "spinner-search",
            },
          }),
        );
      }
    };

    const handleArticleResults = (event) => {
      if (event.detail.type === "header") {
        setArticles(event.detail.articles);
        setArticlesTotal(event.detail.totalResults);
      }
    };
    const handleSuggestionsResults = (event) => {
      if (event.detail.type === "header") {
        setCategorySuggestions(event.detail.collections);
        setKeywordSuggestions(event.detail.suggestions);
      }
    };

    window.addEventListener(
      "searchrequest-products-complete",
      handleProductsResults,
    );
    window.addEventListener(
      "searchrequest-articles-complete",
      handleArticleResults,
    );
    window.addEventListener(
      "searchrequest-suggestions-complete",
      handleSuggestionsResults,
    );


    window.addEventListener('searchrequest', handleSearchRequest);

    // Cleanup the event listeners on component unmount
    return () => {
      window.removeEventListener(
        "searchrequest-products-complete",
        handleProductsResults,
      );
      window.removeEventListener(
        "searchrequest-articles-complete",
        handleArticleResults,
      );
      window.removeEventListener(
        "searchrequest-suggestions-complete",
        handleSuggestionsResults,
      );

      window.removeEventListener('searchrequest', handleSearchRequest);

    };
  }, []);

  return (
      <ReactifySearchProvider
          mode="search"
          shopifyPermanentDomain={`${settings?.shop?.domain}`}
          includeFields={[
            "title",
            "handle",
            "variants.option*", // returns variants.option1, variants.option2, etc...
          ]}
      >
        <div className="header-search-results">
          <div className="header-search-results__container">
            <HeaderKeywords query={query} keywordSuggestions={keywordSuggestions} />

            {query.length > 0 && (
              <div className="flex flex-col lg:flex-row justify-center lg:gap-5 px-4 lg:px-0">
                {/* Sidebar */}
                {showSidebar && (
                  <div className="search-sidebar flex flex-col lg:max-w-[193px] w-full">
                    {(keywordSuggestions?.length > 0 ||
                      categorySuggestions?.length > 0) && (
                      <>
                        <span className="b3 text-[#BBBBBD] mb-4 lg:mb-10">
                          Suggestions
                        </span>
                        <div className="b2 pb-4">
                          {keywordSuggestions?.length > 0 && (
                            <div className="mb-4">
                              <p className="bc">KEYWORD</p>
                              <ul>
                                {keywordSuggestions
                                  .slice(0, 6)
                                  .map((suggestion, index) => (
                                    <li className="py-1" key={index}>
                                      <a href={`/search?q=${suggestion.query}&type=product`}>
                                        {suggestion.query}
                                      </a>
                                    </li>
                                  ))}
                              </ul>
                            </div>
                          )}
                          {categorySuggestions?.length > 0 && (
                            <div>
                              <p className="bc">CATEGORY</p>
                              <ul>
                                {categorySuggestions
                                  .slice(0, 6)
                                  .map((suggestion, index) => (
                                    <li className="py-1" key={index}>
                                      <a href={`/collections/${suggestion.handle}`}>
                                        {suggestion.title}
                                      </a>
                                    </li>
                                  ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Main Content */}
                <div className="w-full lg:px-[40px] flex flex-col gap-y-[24px] lg:gap-12">
                  <LoadingSpinner
                    id="spinner-search"
                    classes={"rounded-b-[30px]"}
                  />
                  <>
                    {productsTotal > 0 || articlesTotal > 0 ? (
                      layoutType === "stacked" ? (
                        <StackedLayout
                          settings={settings}
                          query={query}
                          productsTotal={productsTotal}
                          articles={articles}
                          products={products}
                          articlesTotal={articlesTotal}
                          isMobile={isMobile}
                          showSeeAllProductsAtBottom={showSeeAllProductsAtBottom}
                        />
                      ) : layoutType === "tabbed" ? (
                        <TabbedLayout
                          settings={settings}
                          query={query}
                          productsTotal={productsTotal}
                          handleCategoryClick={handleCategoryClick}
                          articles={articles}
                          products={products}
                          selectedCategory={selectedCategory}
                          articlesTotal={articlesTotal}
                          isMobile={isMobile}
                          showSeeAllProductsAtBottom={showSeeAllProductsAtBottom}
                        />
                      ) : null
                    ) : (
                      <HeaderSearchNoResults settings={settings} query={query} />
                    )}
                  </>
                </div>
              </div>
            )}
          </div>
        </div>

      </ReactifySearchProvider>
  );
};
